// ============================================
// GESTÃO DE EMPRESAS - ADMIN
// src/pages/admin/AdminEmpresas.tsx
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { API_CONFIG } from '../../services/staticDataConfig';

interface Empresa {
  cod_empresa: number;
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  email: string;
  telefone: string;
  cidade: string;
  estado: string;
  plano: string;
  tipo_empresa: 'clinica' | 'psicologo_individual' | 'estudante' | '';
  ativo: boolean;
  data_cadastro: string;
  total_usuarios: number;
  total_consultas: number;
  total_pacientes: number;
  ultima_atividade: string;
}

const TIPOS_EMPRESA_LABELS: Record<string, string> = {
  'clinica': 'Clínica',
  'psicologo_individual': 'Psicólogo Individual',
  'estudante': 'Estudante'
};

function normalizarEmpresa(raw: any): Empresa {
  return {
    cod_empresa: raw?.cod_empresa ?? raw?.codEmpresa ?? raw?.id ?? 0,
    nome_fantasia: raw?.nome_fantasia ?? raw?.nomeFantasia ?? '',
    razao_social: raw?.razao_social ?? raw?.razaoSocial ?? '',
    cnpj: raw?.cnpj ?? '',
    email: raw?.email ?? '',
    telefone: raw?.telefone ?? '',
    cidade: raw?.cidade ?? '',
    estado: raw?.estado ?? '',
    plano: raw?.plano ?? raw?.tipoPlano ?? '',
    tipo_empresa: raw?.tipo_empresa ?? raw?.tipoEmpresa ?? raw?.tipo ?? '',
    ativo: raw?.ativo ?? true,
    data_cadastro: raw?.data_cadastro ?? raw?.dataCadastro ?? raw?.criadoEm ?? raw?.created_at ?? raw?.createdAt ?? '',
    total_usuarios: raw?.total_usuarios ?? raw?.totalUsuarios ?? 0,
    total_consultas: raw?.total_consultas ?? raw?.totalConsultas ?? 0,
    total_pacientes: raw?.total_pacientes ?? raw?.totalPacientes ?? 0,
    ultima_atividade: raw?.ultima_atividade ?? raw?.ultimaAtividade ?? raw?.ultimoAcesso ?? ''
  };
}

interface Paginacao {
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
}

interface Filtros {
  busca: string;
  status: 'todos' | 'ativo' | 'inativo';
  plano: string;
  estado: string;
}

export function AdminEmpresas() {
  const { token, hasPermission } = useAdminAuth();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    pagina: 1,
    limite: 20,
    total: 0,
    totalPaginas: 0
  });
  const [filtros, setFiltros] = useState<Filtros>({
    busca: '',
    status: 'todos',
    plano: '',
    estado: ''
  });
  const [empresaSelecionada, setEmpresaSelecionada] = useState<Empresa | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [ordenacao, setOrdenacao] = useState<{
    campo: keyof Empresa | null;
    direcao: 'asc' | 'desc';
  }>({ campo: null, direcao: 'asc' });

  const carregarEmpresas = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);

      const params = new URLSearchParams();
      params.append('pagina', paginacao.pagina.toString());
      params.append('limite', paginacao.limite.toString());
      
      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.status !== 'todos') params.append('status', filtros.status);
      if (filtros.plano) params.append('plano', filtros.plano);
      if (filtros.estado) params.append('estado', filtros.estado);
      
      // Ordenação server-side
      if (ordenacao.campo) {
        // Mapear campos frontend -> backend
        const campoMap: Record<string, string> = {
          'cod_empresa': 'codEmpresa',
          'nome_fantasia': 'nomeFantasia',
          'total_usuarios': 'totalUsuarios',
          'total_consultas': 'totalConsultas',
          'data_cadastro': 'criadoEm',
          'tipo_empresa': 'tipo'
        };
        const campoBackend = campoMap[ordenacao.campo] || ordenacao.campo;
        params.append('ordenarPor', campoBackend);
        params.append('ordem', ordenacao.direcao.toUpperCase());
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/empresas?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar empresas');
      }

      const data = await response.json();
      
      if (data.sucesso) {
        const lista = Array.isArray(data.dados) ? data.dados : [];
        setEmpresas(lista.map(normalizarEmpresa));
        setPaginacao(prev => ({
          ...prev,
          total: data.paginacao?.total || 0,
          totalPaginas: data.paginacao?.totalPaginas || 0
        }));
      }
    } catch (error) {
      console.error('[AdminEmpresas] Erro:', error);
      setErro('Erro ao carregar lista de empresas');
    } finally {
      setLoading(false);
    }
  }, [token, paginacao.pagina, paginacao.limite, filtros, ordenacao]);

  useEffect(() => {
    carregarEmpresas();
  }, [carregarEmpresas]);

  async function alterarStatusEmpresa(codEmpresa: number, novoStatus: boolean) {
    if (!hasPermission('gerenciar_empresas')) {
      setErro('Você não tem permissão para alterar status de empresas');
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/empresas/${codEmpresa}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ativo: novoStatus })
      });

      if (!response.ok) {
        throw new Error('Erro ao alterar status da empresa');
      }

      const data = await response.json();
      
      if (data.sucesso) {
        setSucesso(`Status da empresa alterado para ${novoStatus ? 'ativo' : 'inativo'}`);
        carregarEmpresas();
        setTimeout(() => setSucesso(null), 3000);
      }
    } catch (error) {
      console.error('[AdminEmpresas] Erro ao alterar status:', error);
      setErro('Erro ao alterar status da empresa');
    }
  }

  async function verDetalhesEmpresa(codEmpresa: number) {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin-sistema/empresas/${codEmpresa}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes da empresa');
      }

      const data = await response.json();
      
      // API pode retornar direto o objeto ou com wrapper sucesso/dados
      if (data.sucesso && data.dados) {
        setEmpresaSelecionada(normalizarEmpresa(data.dados));
        setShowModal(true);
      } else if (data.codEmpresa || data.cod_empresa || data.nomeFantasia || data.nome_fantasia) {
        // Resposta direta sem wrapper
        setEmpresaSelecionada(normalizarEmpresa(data));
        setShowModal(true);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (error) {
      console.error('[AdminEmpresas] Erro ao carregar detalhes:', error);
      setErro('Erro ao carregar detalhes da empresa');
    }
  }

  function handleFiltroChange(campo: keyof Filtros, valor: string) {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  function limparFiltros() {
    setFiltros({
      busca: '',
      status: 'todos',
      plano: '',
      estado: ''
    });
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  function ordenarPor(campo: keyof Empresa) {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
    // Voltar para página 1 ao mudar ordenação
    setPaginacao(prev => ({ ...prev, pagina: 1 }));
  }

  // Ordenação agora é feita no backend, apenas usar empresas direto
  const empresasOrdenadas = empresas;

  function formatarData(dataString: string): string {
    if (!dataString) return '-';
    try {
      const data = new Date(dataString);
      if (isNaN(data.getTime())) return '-';
      return data.toLocaleDateString('pt-BR');
    } catch {
      return '-';
    }
  }

  function formatarCNPJ(cnpj: string): string {
    if (!cnpj) return '';
    const numeros = cnpj.replace(/\D/g, '');
    if (numeros.length !== 14) return cnpj;
    return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-building me-2"></i>
            Gestão de Empresas
          </h2>
          <p className="text-muted mb-0">
            {paginacao.total} empresa{paginacao.total !== 1 ? 's' : ''} encontrada{paginacao.total !== 1 ? 's' : ''}
          </p>
        </div>
        <button 
          className="btn btn-outline-primary"
          onClick={carregarEmpresas}
          disabled={loading}
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
          Atualizar
        </button>
      </div>

      {/* Alertas */}
      {erro && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <div>{erro}</div>
          <button type="button" className="btn-close ms-auto" onClick={() => setErro(null)}></button>
        </div>
      )}
      
      {sucesso && (
        <div className="alert alert-success d-flex align-items-center" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          <div>{sucesso}</div>
          <button type="button" className="btn-close ms-auto" onClick={() => setSucesso(null)}></button>
        </div>
      )}

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Buscar</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nome, CNPJ ou email..."
                  value={filtros.busca}
                  onChange={(e) => handleFiltroChange('busca', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filtros.status}
                onChange={(e) => handleFiltroChange('status', e.target.value as Filtros['status'])}
              >
                <option value="todos">Todos</option>
                <option value="ativo">Ativos</option>
                <option value="inativo">Inativos</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Plano</label>
              <select
                className="form-select"
                value={filtros.plano}
                onChange={(e) => handleFiltroChange('plano', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="gratuito">Gratuito</option>
                <option value="basico">Básico</option>
                <option value="profissional">Profissional</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                value={filtros.estado}
                onChange={(e) => handleFiltroChange('estado', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="SP">SP</option>
                <option value="RJ">RJ</option>
                <option value="MG">MG</option>
                <option value="RS">RS</option>
                <option value="PR">PR</option>
                <option value="SC">SC</option>
                <option value="BA">BA</option>
                <option value="GO">GO</option>
                <option value="DF">DF</option>
                {/* Adicionar mais estados conforme necessário */}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={limparFiltros}
              >
                <i className="fas fa-times me-2"></i>
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Empresas */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('cod_empresa')}>
                    ID {ordenacao.campo === 'cod_empresa' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('nome_fantasia')}>
                    Empresa {ordenacao.campo === 'nome_fantasia' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('tipo_empresa')}>
                    Tipo {ordenacao.campo === 'tipo_empresa' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('cnpj')}>
                    CNPJ {ordenacao.campo === 'cnpj' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => ordenarPor('total_usuarios')}>
                    Usuários {ordenacao.campo === 'total_usuarios' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => ordenarPor('total_consultas')}>
                    Consultas {ordenacao.campo === 'total_consultas' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('ativo')}>
                    Status {ordenacao.campo === 'ativo' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => ordenarPor('data_cadastro')}>
                    Cadastro {ordenacao.campo === 'data_cadastro' && (
                      <i className={`fas fa-sort-${ordenacao.direcao === 'asc' ? 'up' : 'down'} ms-1`}></i>
                    )}
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && empresasOrdenadas.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </td>
                  </tr>
                ) : empresasOrdenadas.length > 0 ? (
                  empresasOrdenadas.map((empresa) => (
                    <tr key={empresa.cod_empresa}>
                      <td>
                        <span className="badge bg-secondary">{empresa.cod_empresa}</span>
                      </td>
                      <td>
                        <strong>{empresa.nome_fantasia}</strong>
                        <br />
                        <small className="text-muted">{empresa.email}</small>
                      </td>
                      <td>
                        <span className={`badge ${
                          empresa.tipo_empresa === 'clinica' ? 'bg-primary' :
                          empresa.tipo_empresa === 'psicologo_individual' ? 'bg-info' :
                          empresa.tipo_empresa === 'estudante' ? 'bg-warning text-dark' :
                          'bg-secondary'
                        }`}>
                          {TIPOS_EMPRESA_LABELS[empresa.tipo_empresa] || 'N/D'}
                        </span>
                      </td>
                      <td>
                        <code className="small">{formatarCNPJ(empresa.cnpj)}</code>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark">
                          {empresa.total_usuarios}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark">
                          {empresa.total_consultas}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${empresa.ativo ? 'bg-success' : 'bg-danger'}`}>
                          {empresa.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <small>{formatarData(empresa.data_cadastro)}</small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button 
                            className="btn btn-outline-primary"
                            title="Ver detalhes"
                            onClick={() => verDetalhesEmpresa(empresa.cod_empresa)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          {hasPermission('gerenciar_empresas') && (
                            <button 
                              className={`btn ${empresa.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                              title={empresa.ativo ? 'Desativar' : 'Ativar'}
                              onClick={() => alterarStatusEmpresa(empresa.cod_empresa, !empresa.ativo)}
                            >
                              <i className={`fas fa-${empresa.ativo ? 'ban' : 'check'}`}></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      <i className="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                      <p className="text-muted mb-0">Nenhuma empresa encontrada</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginação */}
        {paginacao.totalPaginas > 1 && (
          <div className="card-footer bg-transparent">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Mostrando {((paginacao.pagina - 1) * paginacao.limite) + 1} a{' '}
                {Math.min(paginacao.pagina * paginacao.limite, paginacao.total)} de{' '}
                {paginacao.total} registros
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${paginacao.pagina === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginacao(prev => ({ ...prev, pagina: prev.pagina - 1 }))}
                    >
                      Anterior
                    </button>
                  </li>
                  {Array.from({ length: Math.min(5, paginacao.totalPaginas) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <li key={pageNum} className={`page-item ${paginacao.pagina === pageNum ? 'active' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setPaginacao(prev => ({ ...prev, pagina: pageNum }))}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${paginacao.pagina === paginacao.totalPaginas ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginacao(prev => ({ ...prev, pagina: prev.pagina + 1 }))}
                    >
                      Próximo
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {showModal && empresaSelecionada && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-building me-2"></i>
                  Detalhes da Empresa
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-4">
                  <div className="col-md-6">
                    <h6 className="text-muted mb-3">Informações Básicas</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th style={{ width: '40%' }}>Nome Fantasia:</th>
                          <td>{empresaSelecionada.nome_fantasia}</td>
                        </tr>
                        <tr>
                          <th>Razão Social:</th>
                          <td>{empresaSelecionada.razao_social || '-'}</td>
                        </tr>
                        <tr>
                          <th>CNPJ:</th>
                          <td>{formatarCNPJ(empresaSelecionada.cnpj)}</td>
                        </tr>
                        <tr>
                          <th>Email:</th>
                          <td>{empresaSelecionada.email}</td>
                        </tr>
                        <tr>
                          <th>Telefone:</th>
                          <td>{empresaSelecionada.telefone || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted mb-3">Estatísticas</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <div className="bg-primary bg-opacity-10 rounded p-3 text-center">
                          <h4 className="mb-0 text-primary">{empresaSelecionada.total_usuarios}</h4>
                          <small className="text-muted">Usuários</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="bg-success bg-opacity-10 rounded p-3 text-center">
                          <h4 className="mb-0 text-success">{empresaSelecionada.total_pacientes}</h4>
                          <small className="text-muted">Pacientes</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="bg-info bg-opacity-10 rounded p-3 text-center">
                          <h4 className="mb-0 text-info">{empresaSelecionada.total_consultas}</h4>
                          <small className="text-muted">Consultas</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className={`${empresaSelecionada.ativo ? 'bg-success' : 'bg-danger'} bg-opacity-10 rounded p-3 text-center`}>
                          <h4 className="mb-0">{empresaSelecionada.ativo ? '✓' : '✗'}</h4>
                          <small className="text-muted">{empresaSelecionada.ativo ? 'Ativo' : 'Inativo'}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEmpresas;
